import React, { useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { userService } from "services/user/user.service"

interface TwoFactorModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

enum SetupStep {
  LOADING = "LOADING",
  QR_CODE = "QR_CODE",
  VERIFY = "VERIFY",
  BACKUP_CODES = "BACKUP_CODES"
}

export const TwoFactorSetupModal: React.FC<TwoFactorModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [step, setStep] = useState<SetupStep>(SetupStep.LOADING)
  const [qrCode, setQrCode] = useState<string>("")
  const [manualCode, setManualCode] = useState<string>("")
  const [verificationCode, setVerificationCode] = useState<string>("")
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>("")

  React.useEffect(() => {
    if (isOpen) {
      startSetup()
    }
  }, [isOpen])

  const startSetup = async () => {
    try {
      setLoading(true)
      setError("")
      const response = await userService.startTwoFactorSetup()
      if (response) {
        setQrCode(response.qr)
        setManualCode(response.manualCode)
        setStep(SetupStep.QR_CODE)
      }
    } catch (err: any) {
      setError(err?.message ?? "Failed to start 2FA setup")
      toast.error("Failed to start 2FA setup")
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter a valid 6-digit code")
      return
    }

    try {
      setLoading(true)
      setError("")
      const codes = await userService.enableTwoFactor(verificationCode)
      if (codes) {
        setBackupCodes(codes)
        setStep(SetupStep.BACKUP_CODES)
        toast.success("2FA enabled successfully!")
      }
    } catch (err: any) {
      setError(err?.message ?? "Invalid verification code")
      toast.error("Invalid verification code")
    } finally {
      setLoading(false)
    }
  }

  const handleComplete = () => {
    onSuccess()
    handleClose()
  }

  const handleClose = () => {
    setStep(SetupStep.LOADING)
    setQrCode("")
    setManualCode("")
    setVerificationCode("")
    setBackupCodes([])
    setError("")
    onClose()
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success("Copied to clipboard!")
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-grey-dark rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-grey-dark dark:text-white">
            {step === SetupStep.BACKUP_CODES ? t("auth.twoFa.backupCodes") : t("auth.twoFa.enableTitle")}
          </h2>
          <button onClick={handleClose} className="text-grey-dark dark:text-white hover:text-red" disabled={loading}>
            ✕
          </button>
        </div>

        {step === SetupStep.LOADING && (
          <div className="flex justify-center py-8">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        )}

        {step === SetupStep.QR_CODE && (
          <div className="space-y-4">
            <p className="text-grey-dark dark:text-white">{t("auth.twoFa.scanQr")}</p>

            <div className="flex justify-center p-4 bg-white rounded">
              <img src={qrCode} width={200} height={200} alt="QR Code" />
            </div>

            <div className="space-y-2">
              <p className="text-sm text-grey-dark dark:text-white">{t("auth.twoFa.manualEntry")}</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualCode}
                  readOnly
                  className="flex-1 px-3 py-2 border rounded dark:bg-grey-darker dark:text-white dark:border-grey font-mono text-sm"
                />
                <button onClick={() => copyToClipboard(manualCode)} className="px-4 py-2 bg-blue text-white rounded hover:bg-blue-dark">
                  {t("common.copy")}
                </button>
              </div>
            </div>

            <button onClick={() => setStep(SetupStep.VERIFY)} className="w-full px-4 py-2 bg-green text-white rounded hover:bg-green-dark">
              {t("common.next")}
            </button>
          </div>
        )}

        {step === SetupStep.VERIFY && (
          <div className="space-y-4">
            <p className="text-grey-dark dark:text-white">{t("auth.twoFa.enterAuthenticatorCode")}</p>

            <input
              type="text"
              value={verificationCode}
              onChange={(e) => {
                setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                setError("")
              }}
              placeholder="000000"
              className="w-full px-3 py-2 border rounded dark:bg-grey-darker dark:text-white dark:border-grey font-mono text-2xl text-center tracking-widest"
              maxLength={6}
              autoFocus
            />

            {error && <p className="text-red text-sm">{error}</p>}

            <div className="flex gap-2">
              <button
                onClick={() => setStep(SetupStep.QR_CODE)}
                className="flex-1 px-4 py-2 border border-grey rounded hover:bg-grey-light dark:text-white"
                disabled={loading}
              >
                {t("common.back")}
              </button>
              <button
                onClick={handleVerify}
                disabled={loading || verificationCode.length !== 6}
                className="flex-1 px-4 py-2 bg-green text-white rounded hover:bg-green-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t("auth.twoFa.verifying") : t("auth.twoFa.verify")}
              </button>
            </div>
          </div>
        )}

        {step === SetupStep.BACKUP_CODES && (
          <div className="space-y-4">
            <div className="bg-yellow-100 dark:bg-yellow-900 border border-yellow-400 dark:border-yellow-700 rounded p-4">
              <p className="text-sm text-grey-dark dark:text-white font-semibold">{t("auth.twoFa.saveCodesWarning")}</p>
              <p className="text-sm text-grey-dark dark:text-white mt-1">{t("auth.twoFa.saveCodesDesc")}</p>
            </div>

            <div className="bg-grey-lightest dark:bg-grey-darker rounded p-4 space-y-2">
              {backupCodes.map((code, index) => (
                <div key={index} className="font-mono text-sm text-grey-dark dark:text-white">
                  {index + 1}. {code}
                </div>
              ))}
            </div>

            <button
              onClick={() => copyToClipboard(backupCodes.join("\n"))}
              className="w-full px-4 py-2 border border-grey rounded hover:bg-grey-light dark:text-white text-black"
            >
              {t("auth.twoFa.copyAllCodes")}
            </button>

            <button onClick={handleComplete} className="w-full px-4 py-2 bg-green text-white rounded hover:bg-green-dark">
              {t("auth.twoFa.savedMyCodes")}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
